/**
 * Shape the `subtitle` type schema for both subtitle forms — the Volto Add
 * form and the editor sidebar.
 */

import { getLanguageOptions } from './languageOptions';

const HIDDEN_FIELDSETS = ['internals'];
const PROMOTED_FIELDS = ['subtitle_task'];
// Choice fields whose backend vocabulary carries English names only.
const LANGUAGE_FIELDS = ['subtitle_language', 'subtitle_translated_from'];

/**
 * Fields `@subtitle-save` accepts. Must stay in step with
 * `SUBTITLE_METADATA_FIELDS` in the backend's `restapi.py`, which rejects
 * anything else outright.
 */
export const SUBTITLE_METADATA_FIELDS = [
  'title',
  'description',
  'subtitle_language',
  'subtitle_task',
  'subtitle_diarized',
  'subtitle_fillers',
  'subtitle_translated_from',
];

/**
 * A subtitle schema, told apart by its own fields. The reducer that applies
 * this sees every content type's schema and no id to select on.
 *
 * @param {object} schema
 * @returns {boolean}
 */
export function isSubtitleSchema(schema) {
  const properties = schema?.properties;
  return !!(properties?.subtitle_language && properties?.subtitle_task);
}

/**
 * Hide the Internals fieldset — `cmf.ManagePortal`-protected, so it cannot be
 * saved from either form anyway — and promote `subtitle_task` to the first
 * pane, where it will actually be set. Locale-independent.
 *
 * @param {object} schema
 * @returns {object}
 */
export function buildSubtitleFormSchema(schema) {
  if (!schema?.fieldsets || !isSubtitleSchema(schema)) return schema;

  const hidden = new Set();
  const fieldsets = schema.fieldsets
    .filter((fieldset) => {
      if (!HIDDEN_FIELDSETS.includes(fieldset.id)) return true;
      fieldset.fields.forEach((field) => hidden.add(field));
      return false;
    })
    .map((fieldset) => ({
      ...fieldset,
      fields: fieldset.fields.filter(
        (field) =>
          fieldset.id === 'default' || !PROMOTED_FIELDS.includes(field),
      ),
    }));

  const promoted = PROMOTED_FIELDS.filter(
    (field) => schema.properties?.[field],
  );
  const defaultFieldset = fieldsets.find(
    (fieldset) => fieldset.id === 'default',
  );
  if (defaultFieldset && promoted.length > 0) {
    defaultFieldset.fields = [...defaultFieldset.fields, ...promoted];
  }

  const properties = Object.fromEntries(
    Object.entries(schema.properties || {}).filter(
      ([name]) => !hidden.has(name),
    ),
  );

  return {
    ...schema,
    fieldsets: fieldsets.filter((fieldset) => fieldset.fields.length > 0),
    properties,
    required: (schema.required || []).filter((name) => !hidden.has(name)),
  };
}

/**
 * Inline `choices` on the language fields so they read like the CC modal's
 * dropdown instead of the backend vocabulary's English names. Needs a locale,
 * so it belongs where one is available; the vocabulary stays the authority on
 * which values are accepted.
 *
 * @param {object} schema
 * @param {string} locale
 * @returns {object}
 */
export function localizeLanguageChoices(schema, locale) {
  if (!schema?.properties || !isSubtitleSchema(schema)) return schema;

  const choices = getLanguageOptions(locale).map((opt) => [opt.code, opt.name]);
  return {
    ...schema,
    properties: Object.fromEntries(
      Object.entries(schema.properties).map(([name, field]) =>
        LANGUAGE_FIELDS.includes(name) && field?.vocabulary
          ? [name, { ...field, choices }]
          : [name, field],
      ),
    ),
  };
}

export default buildSubtitleFormSchema;
